import { useState } from "react";
import { ArrowRightLeft, Link2, Plus } from "lucide-react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import type { CedenteDoSistema, Sistema } from "@/api/contrato";
import { useSessao } from "@/sessao/useSessao";
import {
  useAlterarCobranca,
  useCedentes,
  usePessoa,
  useVinculosDoBeneficiario,
} from "@/api/consultas";
import { recursoInexistente } from "@/api/problema";
import { CaixaDeCampo } from "@/componentes/CaixaDeCampo";
import { Botao } from "@/componentes/Botao";
import { Dica } from "@/componentes/Dica";
import { Esqueleto } from "@/componentes/Esqueleto";
import { FaixaDeErro } from "@/componentes/FaixaDeErro";
import { NaoEncontrado } from "@/componentes/NaoEncontrado";
import { PainelLateral } from "@/componentes/PainelLateral";
import { useAvisos } from "@/componentes/Avisos";
import { Confirmacao } from "@/componentes/Confirmacao";
import { documento } from "@/formato";
import { rotaDaAplicacao, rotaDoCedente, rotaDoCedenteDaAplicacao } from "@/rotas";
import { MotivoDoPar } from "@/telas/cedentes/DetalheDoVinculo";
import { ROTULO_DA_MODALIDADE, consumoDoPar, resumoDosConvenios } from "./cedente";
import { SituacaoDoPar } from "./SituacaoDoPar";
import { NovoVinculo } from "./NovoVinculo";
import { NovoContrato } from "./NovoContrato";
import { PlanoPadrao } from "./PlanoPadrao";
import { DetalheDoCedente } from "./DetalheDoCedente";
import { useAplicacaoDaRota } from "./useAplicacaoDaRota";

/*
 * `/aplicacoes/:id/cedentes`, da change `add-cobranca-por-cedente`.
 *
 * Em cima, quem paga (a modalidade) e o plano do cedente novo; embaixo, os vínculos. A
 * linha é link real para o painel do par, e o `Outlet` é esse painel: rota filha, com a
 * aba montada embaixo, como o vínculo sobre a aba Aplicações do cedente.
 */
export function AbaDeCedentes() {
  const detalhe = useAplicacaoDaRota();
  const { cliente } = useSessao();
  const { data, error, isPending } = useCedentes(cliente, detalhe.sistema.id, true);

  return (
    <div className="flex flex-col gap-4">
      {error ? (
        <FaixaDeErro erro={error} />
      ) : (
        <CedentesDaAplicacao
          sistema={detalhe.sistema}
          cedentes={data?.cedentes ?? []}
          carregando={isPending}
        />
      )}

      {/*
        Fora do ramo do erro, e sempre no mesmo lugar da árvore: o painel lê a lista do
        cedente, e uma releitura desta que falha não pode remontá-lo com um modal aberto.
      */}
      <Outlet context={detalhe} />
    </div>
  );
}

function CedentesDaAplicacao({
  sistema,
  cedentes,
  carregando,
}: {
  sistema: Sistema;
  cedentes: readonly CedenteDoSistema[];
  carregando: boolean;
}) {
  const { pessoaId } = useParams();
  const { cliente, pode } = useSessao();
  const avisos = useAvisos();
  const ehAdmin = pode("admin");
  // O "« voltar" da página continua sabendo de que lista o operador veio.
  const estado: unknown = useLocation().state;

  const alterar = useAlterarCobranca(cliente, sistema.id);

  const modalidade = sistema.cobranca.modalidade_cobranca;
  const porCedente = modalidade === "por_cedente";

  const [modal, setModal] = useState<"vinculo" | "plano-padrao" | null>(null);
  const [contratoPara, setContratoPara] = useState<CedenteDoSistema | null>(null);
  const [trocandoModalidade, setTrocandoModalidade] = useState(false);

  const destino = porCedente ? "por_sistema" : "por_cedente";

  return (
    <>
      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <CaixaDeCampo rotulo="Cobrança">
          <span className="flex items-center gap-1">
            {ROTULO_DA_MODALIDADE[modalidade]}
            <Dica
              texto={
                porCedente
                  ? "Cada cedente paga pelo contrato do vínculo. O contrato da aplicação vale como teto de proteção e não gera fatura."
                  : "A aplicação paga pelo contrato dela. O cedente só tem o teto próprio, quando declarado."
              }
            />
          </span>
        </CaixaDeCampo>
        {porCedente && (
          <CaixaDeCampo rotulo="Plano padrão">
            {sistema.cobranca.plano_padrao_cedente?.nome ?? null}
          </CaixaDeCampo>
        )}
      </div>

      {ehAdmin && (
        <div className="flex flex-wrap gap-2">
          <Botao onClick={() => setModal("vinculo")}>
            <Link2 aria-hidden="true" className="size-4" />
            Vincular cedente
          </Botao>
          {porCedente && (
            <Botao tom="secundario" onClick={() => setModal("plano-padrao")}>
              Plano padrão
            </Botao>
          )}
          <Botao tom="secundario" onClick={() => setTrocandoModalidade(true)}>
            <ArrowRightLeft aria-hidden="true" className="size-4" />
            {destino === "por_cedente" ? "Cobrar por cedente" : "Cobrar pela aplicação"}
          </Botao>
        </div>
      )}

      {!carregando && cedentes.length === 0 ? (
        <p className="text-texto-2">Nenhum cedente.</p>
      ) : (
        <ul aria-label="Cedentes" className="flex flex-col gap-1">
          {cedentes.map((cedente) => {
            const semContrato = cedente.motivo_nao_emite === "vinculo_sem_contrato";
            const aberto = cedente.pessoa_id === pessoaId;

            return (
              <li
                key={cedente.pessoa_id}
                className={[
                  "flex items-center gap-2 rounded-controle border bg-superficie-2 px-3 py-2",
                  semContrato ? "border-falha/40" : "border-fio",
                  aberto ? "shadow-[inset_2px_0_0_var(--esferografica)]" : "",
                ].join(" ")}
              >
                <Link
                  to={rotaDoCedenteDaAplicacao(sistema.id, cedente.pessoa_id)}
                  state={estado}
                  aria-current={aberto}
                  className="flex min-w-0 flex-1 flex-col items-start text-left"
                >
                  <span className="w-full truncate text-texto">{cedente.razao_social}</span>
                  <span className="dados w-full truncate text-[12px] text-texto-2">
                    {documento(cedente.documento)} ·{" "}
                    {porCedente
                      ? (cedente.contrato?.plano_nome ?? "sem contrato")
                      : `convênios: ${resumoDosConvenios(cedente.convenios_permitidos)}`}{" "}
                    · {consumoDoPar(cedente.consumo)}
                  </span>
                </Link>

                <SituacaoDoPar emite={cedente.emite} motivo={cedente.motivo_nao_emite} />

                {/*
                  A ação de contrato fica AO LADO do "não emite" (spec, cenário
                  "Cedente sem contrato destacado"): quem vê a clínica parada
                  resolve dali, sem abrir o cedente.
                */}
                {ehAdmin && semContrato && (
                  <Botao
                    tom="secundario"
                    aria-label={`Novo contrato para ${cedente.razao_social}`}
                    onClick={() => setContratoPara(cedente)}
                  >
                    <Plus aria-hidden="true" className="size-4" />
                    Contrato
                  </Botao>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {modal === "vinculo" && (
        <NovoVinculo
          sistemaID={sistema.id}
          sistemaNome={sistema.nome}
          jaVinculados={new Set(cedentes.map((cedente) => cedente.pessoa_id))}
          aoFechar={() => setModal(null)}
        />
      )}

      {modal === "plano-padrao" && (
        <PlanoPadrao sistema={sistema} aoFechar={() => setModal(null)} />
      )}

      {contratoPara !== null && (
        <NovoContrato
          escopo={{ sistemaID: sistema.id, pessoaID: contratoPara.pessoa_id }}
          nome={contratoPara.razao_social}
          aoFechar={() => setContratoPara(null)}
        />
      )}

      {/*
        ┌────────────────────────────────────────────────────────────────────┐
        │ CONFIRMAÇÃO CRÍTICA: A MODALIDADE DECIDE QUEM PAGA CADA BOLETO.    │
        │                                                                    │
        │ Trocar de pagador no meio da competência não fecha fatura, e o     │
        │ servidor recusa com `409 modalidade_com_consumo` — a recusa        │
        │ aparece DENTRO do diálogo, com o texto dele, e o diálogo não fecha.│
        └────────────────────────────────────────────────────────────────────┘
      */}
      <Confirmacao
        pedido={
          trocandoModalidade
            ? {
                severidade: "critica",
                titulo:
                  destino === "por_cedente"
                    ? `Cobrar ${sistema.nome} por cedente?`
                    : `Cobrar ${sistema.nome} pela aplicação?`,
                corpo:
                  destino === "por_cedente"
                    ? "Cada cedente passa a precisar de contrato próprio para emitir."
                    : "A aplicação volta a pagar pelo contrato dela, e o plano padrão sai.",
                verbo: destino === "por_cedente" ? "Cobrar por cedente" : "Cobrar pela aplicação",
                digitarParaLiberar: sistema.nome,
              }
            : null
        }
        aoCancelar={() => setTrocandoModalidade(false)}
        aoConfirmar={async () => {
          await alterar.mutateAsync({ modalidade_cobranca: destino });
          setTrocandoModalidade(false);
          avisos.feito("Modalidade alterada");
        }}
      />
    </>
  );
}

/*
 * `/aplicacoes/:id/cedentes/:pessoaId`: o par, em painel sobre a aba.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ O VÍNCULO VEM DA LISTA DO CEDENTE, E NÃO DA LISTA DA APLICAÇÃO.          │
 * │                                                                          │
 * │ `GET /sistemas/{id}/vinculos` para em 200 linhas, em ordem de razão      │
 * │ social: o link colado da clínica 201 diria que um vínculo vigente não    │
 * │ existe. É o mesmo motivo de `DetalheDoVinculo`, visto do outro lado, e   │
 * │ toda escrita do par já invalida a lista do cedente.                      │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Cedente que não é desta aplicação, ou que não existe, responde igual: o caminho não leva
 * a nada, e a aba continua ali atrás. Fechar leva o `state`, para o "« voltar" da página.
 */
export function CedenteDaAplicacao() {
  const { sistema } = useAplicacaoDaRota();
  const { pessoaId = "" } = useParams();
  const { cliente } = useSessao();
  const navegar = useNavigate();
  const estado: unknown = useLocation().state;

  const pessoa = usePessoa(cliente, pessoaId);
  const vinculos = useVinculosDoBeneficiario(cliente, pessoaId);
  const vinculo = vinculos.data?.vinculos.find((item) => item.sistema_id === sistema.id);

  const inexistente =
    recursoInexistente(pessoa.error) ||
    recursoInexistente(vinculos.error) ||
    (vinculos.data !== undefined && vinculo === undefined);
  const falha = inexistente ? null : (pessoa.error ?? vinculos.error);

  function fechar() {
    void navegar(rotaDaAplicacao(sistema.id, "cedentes"), { state: estado });
  }

  if (pessoa.data === undefined || vinculo === undefined) {
    return (
      <PainelLateral
        aberto
        aoFechar={fechar}
        descricao="Detalhe do cedente"
        titulo={
          inexistente || falha !== null ? (
            <span className="font-semibold text-texto">Cedente</span>
          ) : (
            <Esqueleto className="h-5 w-40" />
          )
        }
      >
        <div aria-busy={!inexistente && falha === null} className="p-4">
          {inexistente ? (
            <NaoEncontrado
              texto="Cedente não encontrado."
              para={rotaDaAplicacao(sistema.id, "cedentes")}
              rotuloDoLink="Cedentes"
            />
          ) : falha !== null ? (
            <FaixaDeErro erro={falha} />
          ) : (
            <Esqueleto className="h-40" />
          )}
        </div>
      </PainelLateral>
    );
  }

  const { razao_social: razaoSocial, documento: documentoDoCedente } = pessoa.data;

  return (
    <PainelLateral
      aberto
      aoFechar={fechar}
      descricao={`Vínculo de ${razaoSocial} com ${sistema.nome}`}
      titulo={
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            to={rotaDoCedente(pessoaId)}
            className="truncate text-titulo font-semibold text-texto underline-offset-2 hover:underline"
          >
            {razaoSocial}
          </Link>
          <span
            className={`dados truncate text-[12px] ${
              vinculo.status === "suspenso" ? "text-transito" : "text-texto-2"
            }`}
          >
            {documento(documentoDoCedente)}
            {/* Estado normal não se anuncia. */}
            {vinculo.status === "ativo" ? (
              <span className="sr-only"> · ativo</span>
            ) : (
              ` · ${vinculo.status}`
            )}
          </span>
          <MotivoDoPar motivo={vinculo.motivo_nao_emite} />
        </div>
      }
    >
      <div className="p-4">
        <DetalheDoCedente
          sistema={sistema}
          cedente={{ ...vinculo, pessoa_id: pessoaId, razao_social: razaoSocial }}
        />
      </div>
    </PainelLateral>
  );
}
